import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { attachActiveAiTurn, qk, streamAiTurn } from "@vorloop/api-client-react";
import { TurnView } from "./TurnView";
import type { ChatToolCall, ChatTurn } from "./types";
import { IconPlus, IconTarget, IconUp, IconX } from "../layout/Icons";
import {
  MAX_ATTACHMENTS,
  Thumbnail,
  extractPastedImages,
  fileToAttachment,
  hasDraggedFiles,
  type Attachment,
} from "./attachments";

const FILE_WRITING_TOOLS = new Set(["write_file", "edit_file", "delete_file", "add_dependency"]);

/**
 * The chat dock per spec/05-ide.md §3.
 * Replit-style: header with title + agent badge, scrollable turn
 * list, pill composer with Economy/Plan toggles + send.
 */
export function ChatDock({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [sending, setSending] = useState(false);
  const [planMode, setPlanMode] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  /** True while files are being decoded/resized so we can disable send. */
  const [pickingFiles, setPickingFiles] = useState(false);
  // Snapshot of attachments handed to the current in-flight turn — used
  // by the auto-retry path so the Sonnet retry sends the same images.
  const lastAttachmentsRef = useRef<Attachment[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const turnIdRef = useRef(0);
  const sendRef = useRef<(
    promptOverride?: string,
    modelOverride?: string,
    attachmentsOverride?: Attachment[],
  ) => Promise<void>>(async () => {});
  // Ref-based guard so send()'s "already in flight" check is not at
  // the mercy of React's render timing. The `sending` state is purely
  // for UI; this ref is the actual gate.
  const inFlightRef = useRef(false);

  // Listen for prefill events from buttons elsewhere in the editor that
  // pre-load a prompt (e.g. CanvasBoard's Generate menu).
  useEffect(() => {
    function onPrefill(e: Event) {
      const detail = (e as CustomEvent<{ prompt: string }>).detail;
      if (detail?.prompt) setPrompt(detail.prompt);
    }
    window.addEventListener("vp:chat:prefill", onPrefill);
    return () => window.removeEventListener("vp:chat:prefill", onPrefill);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns]);

  // On mount: try to reattach to any in-flight turn first. If one is
  // running (the user reloaded mid-stream, or has the project open in
  // another tab), we replay its buffered events into a synthetic turn
  // row and tail the rest. If nothing is active, fall through to the
  // first-prompt hand-off from the Dashboard.
  useEffect(() => {
    let aborted = false;
    (async () => {
      const id = `turn_${++turnIdRef.current}`;
      let bound: ChatTurn | null = null;

      const handlers = {
        onAssistantText: (text: string) => {
          if (!bound) {
            bound = {
              id,
              userPrompt: "(reattached turn)",
              assistantText: text,
              toolCalls: [],
              status: "streaming" as const,
              iterations: 0,
              startedAt: Date.now(),
            };
            setTurns((arr) => [...arr, bound!]);
          } else {
            setTurns((arr) => arr.map((t) => (t.id === id ? { ...t, assistantText: text } : t)));
          }
        },
        onToolCall: (call: { id: string; name: string; args?: unknown }) => {
          if (!bound) {
            bound = {
              id,
              userPrompt: "(reattached turn)",
              assistantText: "",
              toolCalls: [],
              status: "streaming" as const,
              iterations: 0,
              startedAt: Date.now(),
            };
            setTurns((arr) => [...arr, bound!]);
          }
          const tc: ChatToolCall = { id: call.id, name: call.name, args: call.args };
          setTurns((arr) =>
            arr.map((t) => (t.id === id ? { ...t, toolCalls: [...t.toolCalls, tc] } : t)),
          );
        },
        onToolResult: (call: { id: string; name: string; result?: string; ok?: boolean }) => {
          setTurns((arr) =>
            arr.map((t) =>
              t.id === id
                ? {
                    ...t,
                    toolCalls: t.toolCalls.map((c) =>
                      c.id === call.id ? { ...c, result: call.result, ok: call.ok } : c,
                    ),
                  }
                : t,
            ),
          );
          if (call.ok !== false && FILE_WRITING_TOOLS.has(call.name)) {
            qc.invalidateQueries({ queryKey: qk.projectFiles(projectId) });
          }
        },
        onTurnComplete: (e: { assistantText: string; iterations: number }) => {
          setTurns((arr) =>
            arr.map((t) =>
              t.id === id
                ? { ...t, assistantText: e.assistantText, status: "complete", iterations: e.iterations }
                : t,
            ),
          );
        },
        onError: (message: string) => {
          setTurns((arr) =>
            arr.map((t) => (t.id === id ? { ...t, status: "error", error: message } : t)),
          );
        },
      };

      try {
        setSending(true);
        const attached = await attachActiveAiTurn({ projectId, handlers });
        if (aborted) return;
        // Clear `sending` BEFORE the first-prompt hand-off; otherwise
        // send()'s guard (`if (!trimmed || sending) return`) eats the
        // call and the agent never starts.
        setSending(false);
        if (!attached) {
          // Nothing in flight — fall through to first-prompt hand-off.
          let firstPrompt: string | null = null;
          let firstAttachments: Attachment[] | undefined;
          try {
            firstPrompt = sessionStorage.getItem(`vp.firstPrompt.${projectId}`);
            if (firstPrompt) sessionStorage.removeItem(`vp.firstPrompt.${projectId}`);
            const rawAtt = sessionStorage.getItem(`vp.firstAttachments.${projectId}`);
            if (rawAtt) {
              sessionStorage.removeItem(`vp.firstAttachments.${projectId}`);
              const parsed: unknown = JSON.parse(rawAtt);
              if (Array.isArray(parsed)) firstAttachments = parsed as Attachment[];
            }
          } catch {
            // ignore (private mode, parse failure)
          }
          if (firstPrompt && firstPrompt.trim()) {
            void sendRef.current(firstPrompt.trim(), undefined, firstAttachments);
          }
        }
      } catch {
        if (!aborted) setSending(false);
      }
    })();
    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function send(
    promptOverride?: string,
    modelOverride?: string,
    attachmentsOverride?: Attachment[],
  ) {
    const trimmed = (promptOverride ?? prompt).trim();
    // Use a ref for the in-flight guard so callers fired during the
    // same React tick don't lose to a stale `sending` closure.
    if (!trimmed || inFlightRef.current) return;
    inFlightRef.current = true;
    setSending(true);
    const turnAttachments = attachmentsOverride ?? attachments;
    lastAttachmentsRef.current = turnAttachments;

    const id = `turn_${++turnIdRef.current}`;
    const initial: ChatTurn = {
      id,
      userPrompt: trimmed,
      assistantText: "",
      toolCalls: [],
      status: "streaming",
      iterations: 0,
      startedAt: Date.now(),
      ...(modelOverride && { modelOverride }),
      ...(turnAttachments.length > 0 && {
        images: turnAttachments.map((a) => ({ dataUrl: a.dataUrl, name: a.name })),
      }),
    };
    setTurns((arr) => [...arr, initial]);
    // Only clear the composer when the user typed it themselves —
    // first-prompt hand-off keeps the composer empty already.
    if (promptOverride === undefined) {
      setPrompt("");
      setAttachments([]);
    }

    function updateTurn(patch: (t: ChatTurn) => ChatTurn) {
      setTurns((arr) => arr.map((t) => (t.id === id ? patch(t) : t)));
    }

    try {
      await streamAiTurn({
        projectId,
        body: {
          conversationId,
          prompt: trimmed,
          ...(modelOverride && { modelOverride }),
          ...(turnAttachments.length > 0 && {
            imageUrls: turnAttachments.map((a) => a.dataUrl),
          }),
        },
        handlers: {
          onConversation: (c) => setConversationId(c.id),
          onAssistantText: (text) => updateTurn((t) => ({ ...t, assistantText: text })),
          onToolCall: (call) => {
            const tc: ChatToolCall = { id: call.id, name: call.name, args: call.args };
            updateTurn((t) => ({ ...t, toolCalls: [...t.toolCalls, tc] }));
          },
          onToolResult: (call) => {
            updateTurn((t) => ({
              ...t,
              toolCalls: t.toolCalls.map((c) =>
                c.id === call.id ? { ...c, result: call.result, ok: call.ok } : c,
              ),
            }));
            if (call.ok !== false && FILE_WRITING_TOOLS.has(call.name)) {
              qc.invalidateQueries({ queryKey: qk.projectFiles(projectId) });
              qc.invalidateQueries({ queryKey: ["projects", projectId, "files", "read"] });
            }
          },
          onTurnComplete: (e) => {
            updateTurn((t) => ({
              ...t,
              assistantText: e.assistantText,
              status: "complete",
              iterations: e.iterations,
              endedAt: Date.now(),
            }));
          },
          onError: (message, code) => {
            updateTurn((t) => ({
              ...t,
              status: "error",
              error: message,
              ...(code && { errorCode: code }),
              endedAt: Date.now(),
            }));

            // Auto-fallback: a free-tier turn that timed out (Kimi via
            // Novita typically) re-runs on Sonnet 4.5 automatically.
            // We gate on (a) it's a timeout, (b) we haven't already
            // tried Sonnet on this turn, and (c) we weren't already
            // explicitly on Sonnet.
            const isTimeout = code === "turn_aborted" || /timed out/i.test(message);
            const alreadyOnSonnet = (modelOverride ?? "") === "sonnet-4.5";
            if (isTimeout && !alreadyOnSonnet) {
              updateTurn((t) => ({
                ...t,
                error: message + " — auto-retrying on Sonnet 4.5…",
                autoRetried: true,
              }));
              // Fire after a tick so React commits the in-flight reset.
              setTimeout(() => {
                inFlightRef.current = false;
                void sendRef.current(trimmed, "sonnet-4.5", lastAttachmentsRef.current);
              }, 250);
            }
          },
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Stream failed";
      updateTurn((t) => ({ ...t, status: "error", error: message, endedAt: Date.now() }));
    } finally {
      inFlightRef.current = false;
      setSending(false);
    }
  }
  sendRef.current = send;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        background: "var(--bg-panel)",
      }}
    >
      <ChatHeader />

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "12px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {turns.length === 0 ? (
          <div style={{ color: "var(--text-tertiary)", fontSize: 12 }}>
            Describe what you want to build or change.
          </div>
        ) : (
          turns.map((t) => (
            <TurnView
              key={t.id}
              turn={t}
              onRetry={() => void sendRef.current(t.userPrompt, "sonnet-4.5")}
              onPickPlanOption={(promptText) => void sendRef.current(promptText)}
            />
          ))
        )}
      </div>

      <Composer
        prompt={prompt}
        onChange={setPrompt}
        onSubmit={() => void send()}
        sending={sending}
        planMode={planMode}
        onPlanModeChange={setPlanMode}
        attachments={attachments}
        pickingFiles={pickingFiles}
        onAddFiles={async (files) => {
          if (files.length === 0) return;
          setPickingFiles(true);
          try {
            const room = MAX_ATTACHMENTS - attachments.length;
            const slice = files.slice(0, Math.max(0, room));
            // allSettled so one unreadable file (e.g. HEIC in browsers that
            // can't decode it) doesn't drop the others.
            const settled = await Promise.allSettled(slice.map(fileToAttachment));
            const added: Attachment[] = [];
            settled.forEach((r, i) => {
              if (r.status === "fulfilled") added.push(r.value);
              else
                console.warn(
                  `Failed to attach ${slice[i]?.name ?? "image"}:`,
                  r.reason,
                );
            });
            if (added.length > 0)
              setAttachments((arr) => [...arr, ...added].slice(0, MAX_ATTACHMENTS));
          } finally {
            setPickingFiles(false);
          }
        }}
        onRemoveAttachment={(id) =>
          setAttachments((arr) => arr.filter((a) => a.id !== id))
        }
      />
    </div>
  );
}

function ChatHeader() {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 14px",
        borderBottom: "1px solid var(--border-default)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 18,
          height: 18,
          borderRadius: 5,
          background: "linear-gradient(135deg,#8B5CF6,#6366F1)",
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2 14 9 21 11 14 13 12 20 10 13 3 11 10 9Z" />
        </svg>
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", flex: 1 }}>
        Project chat
      </span>
      <button type="button" aria-label="Refresh" style={ghostIcon}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" />
        </svg>
      </button>
      <button type="button" aria-label="Close" style={ghostIcon}>
        <IconX />
      </button>
    </header>
  );
}

function Composer({
  prompt,
  onChange,
  onSubmit,
  sending,
  planMode,
  onPlanModeChange,
  attachments,
  pickingFiles,
  onAddFiles,
  onRemoveAttachment,
}: {
  prompt: string;
  onChange: (s: string) => void;
  onSubmit: () => void;
  sending: boolean;
  planMode: boolean;
  onPlanModeChange: (v: boolean) => void;
  attachments: Attachment[];
  pickingFiles: boolean;
  onAddFiles: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Array<{ selector: string; text?: string }>>([]);
  const atCap = attachments.length >= MAX_ATTACHMENTS;
  const sendDisabled = sending || pickingFiles || !prompt.trim();

  // Select-element flow: ChatDock and PreviewIframe communicate via
  // window-level custom events so there's no prop drilling through
  // the editor layout.
  //  start  → PreviewIframe installs an inspector inside its iframe
  //  resolve→ fires when the user clicks an element in the preview
  //  cancel → fires on Esc, or if user toggles the button off
  useEffect(() => {
    if (!selecting) return;
    function onResolve(e: Event) {
      const detail = (e as CustomEvent<{ items?: Array<{ selector: string; text?: string }> }>).detail;
      const items = detail?.items ?? [];
      if (items.length === 0) return;
      setSelectedItems(items);
      setSelecting(false);
    }
    function onCancel() {
      setSelecting(false);
    }
    window.dispatchEvent(new CustomEvent("vp:select-element:start"));
    window.addEventListener("vp:select-element:resolve", onResolve);
    window.addEventListener("vp:select-element:cancel", onCancel);
    return () => {
      window.removeEventListener("vp:select-element:resolve", onResolve);
      window.removeEventListener("vp:select-element:cancel", onCancel);
      // If we're leaving selecting mode for any reason, let listeners
      // tear down their iframe inspectors.
      window.dispatchEvent(new CustomEvent("vp:select-element:cancel"));
    };
  }, [selecting]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (sendDisabled) return;
        if (selectedItems.length > 0) {
          // Stage the selectors + their visible text into the prompt
          // so the agent has both a CSS handle and the human label.
          const stage = selectedItems
            .map((it, i) => {
              const t = it.text ? ` "${it.text.replace(/"/g, "'")}"` : "";
              return `[selected ${i + 1}: ${it.selector}${t}]`;
            })
            .join(" ");
          onChange(`${stage} ${prompt}`);
          setSelectedItems([]);
          // Defer so the prompt update flushes before submit reads it.
          setTimeout(() => onSubmit(), 0);
          return;
        }
        onSubmit();
      }}
      onDragEnter={(e) => {
        if (hasDraggedFiles(e.dataTransfer.types)) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragOver={(e) => {
        if (hasDraggedFiles(e.dataTransfer.types)) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragging(false);
      }}
      onDrop={(e) => {
        if (!hasDraggedFiles(e.dataTransfer.types)) return;
        e.preventDefault();
        setDragging(false);
        const files = Array.from(e.dataTransfer.files).filter((f) =>
          f.type.startsWith("image/"),
        );
        if (files.length > 0) onAddFiles(files);
      }}
      style={{
        padding: 10,
        borderTop: "1px solid var(--border-default)",
        position: "relative",
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          // Reset so picking the same file twice still fires onChange.
          e.target.value = "";
          if (files.length > 0) onAddFiles(files);
        }}
      />
      <div
        style={{
          background: "var(--bg-panel-2)",
          border: `1px solid ${dragging ? "var(--accent-blue)" : "var(--border-default)"}`,
          borderRadius: 12,
          padding: 8,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          transition: "border-color 120ms",
        }}
      >
        {attachments.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              padding: "2px 2px 4px",
            }}
          >
            {attachments.map((a) => (
              <Thumbnail key={a.id} att={a} onRemove={() => onRemoveAttachment(a.id)} />
            ))}
            {pickingFiles && (
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 8,
                  border: "1px dashed var(--border-default)",
                  display: "grid",
                  placeItems: "center",
                  color: "var(--text-tertiary)",
                  fontSize: 10,
                }}
              >
                …
              </div>
            )}
          </div>
        )}
        {selecting && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 10px",
              borderRadius: 8,
              background: "rgba(99,102,241,0.10)",
              border: `1px solid var(--accent-blue)`,
            }}
          >
            <span aria-hidden style={{ fontSize: 13 }}>🎯</span>
            <span style={{ flex: 1, fontSize: 12 }}>
              <strong style={{ color: "var(--accent-blue)" }}>Click elements</strong>{" "}
              <span style={{ color: "var(--text-tertiary)" }}>
                in the preview · click Done when finished · Esc to cancel
              </span>
            </span>
            <button
              type="button"
              onClick={() => setSelecting(false)}
              aria-label="Cancel selecting"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-tertiary)",
                cursor: "pointer",
                padding: 0,
                display: "inline-flex",
              }}
            >
              <IconX size={11} />
            </button>
          </div>
        )}
        {!selecting && selectedItems.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              padding: "4px 2px",
            }}
          >
            {selectedItems.map((it, idx) => (
              <span
                key={idx}
                title={it.selector}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "3px 8px 3px 6px",
                  background: "rgba(236,72,153,0.12)",
                  border: "1px solid rgba(236,72,153,0.45)",
                  borderRadius: 999,
                  fontSize: 11,
                  color: "var(--text-primary)",
                  maxWidth: 280,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 999,
                    background: "#ec4899",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 9,
                    fontWeight: 700,
                  }}
                >
                  {idx + 1}
                </span>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 180,
                  }}
                >
                  {it.text || it.selector}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedItems((arr) => arr.filter((_, i) => i !== idx))
                  }
                  aria-label="Remove selection"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--text-tertiary)",
                    cursor: "pointer",
                    padding: 0,
                    display: "inline-flex",
                  }}
                >
                  <IconX size={9} />
                </button>
              </span>
            ))}
          </div>
        )}
        <textarea
          value={prompt}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              if (!sendDisabled) onSubmit();
            }
          }}
          onPaste={(e) => {
            const files = extractPastedImages(e.clipboardData.items);
            if (files.length > 0) {
              e.preventDefault();
              onAddFiles(files);
            }
          }}
          placeholder={
            attachments.length > 0
              ? "Describe what to do with these images…"
              : "Make, fast, iterate... (drag/paste images too)"
          }
          rows={2}
          disabled={sending}
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            outline: "none",
            color: "var(--text-primary)",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            resize: "none",
            minHeight: 38,
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            aria-label="Attach image"
            onClick={() => fileInputRef.current?.click()}
            disabled={atCap || pickingFiles}
            title={atCap ? `Max ${MAX_ATTACHMENTS} attachments` : "Attach images"}
            style={{
              ...composerIcon,
              cursor: atCap || pickingFiles ? "not-allowed" : "pointer",
              opacity: atCap ? 0.5 : 1,
            }}
          >
            <IconPlus size={14} />
          </button>
          <button
            type="button"
            aria-label="Select elements"
            onClick={() => {
              if (selecting) {
                setSelecting(false);
                return;
              }
              if (selectedItems.length > 0) {
                setSelectedItems([]);
                return;
              }
              setSelecting(true);
            }}
            title={
              selecting
                ? "Click elements in the preview, then click Done"
                : selectedItems.length > 0
                ? `${selectedItems.length} selected — click to clear all`
                : "Select elements in the preview"
            }
            style={{
              ...composerIcon,
              background: selecting
                ? "var(--accent-blue)"
                : selectedItems.length > 0
                ? "var(--bg-active)"
                : "transparent",
              color: selecting ? "#fff" : "var(--text-secondary)",
              borderColor:
                selecting || selectedItems.length > 0
                  ? "var(--accent-blue)"
                  : "var(--border-default)",
            }}
          >
            <IconTarget size={14} />
          </button>
          {attachments.length > 0 && (
            <span
              style={{
                fontSize: 10,
                color: "var(--text-tertiary)",
                fontVariantNumeric: "tabular-nums",
              }}
              title={`${attachments.length} of ${MAX_ATTACHMENTS} attached`}
            >
              {attachments.length}/{MAX_ATTACHMENTS}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <Pill
            active={!planMode}
            onClick={() => onPlanModeChange(false)}
            title="Economy mode uses the free-tier model"
          >
            Economy
          </Pill>
          <Pill active={planMode} onClick={() => onPlanModeChange(true)} title="Plan mode discusses before editing">
            Plan
          </Pill>
          <button
            type="submit"
            disabled={sendDisabled}
            aria-label="Send"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              border: "none",
              background: sendDisabled ? "var(--bg-active)" : "var(--accent-blue)",
              color: sendDisabled ? "var(--text-tertiary)" : "#fff",
              cursor: sendDisabled ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconUp />
          </button>
        </div>
      </div>
      {dragging && (
        <div
          style={{
            position: "absolute",
            inset: 10,
            background: "rgba(99,102,241,0.08)",
            border: "1px dashed var(--accent-blue)",
            borderRadius: 12,
            display: "grid",
            placeItems: "center",
            color: "var(--accent-blue)",
            fontSize: 12,
            fontWeight: 500,
            pointerEvents: "none",
          }}
        >
          Drop images to attach
        </div>
      )}
    </form>
  );
}


function Pill({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        padding: "3px 10px",
        background: active ? "var(--bg-active)" : "transparent",
        color: active ? "var(--text-primary)" : "var(--text-tertiary)",
        border: `1px solid ${active ? "var(--border-strong)" : "var(--border-default)"}`,
        borderRadius: 999,
        fontSize: 11,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

const ghostIcon: React.CSSProperties = {
  width: 24,
  height: 24,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  color: "var(--text-tertiary)",
  cursor: "pointer",
  borderRadius: 6,
};

const composerIcon: React.CSSProperties = {
  width: 26,
  height: 26,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "1px solid var(--border-default)",
  color: "var(--text-secondary)",
  borderRadius: 7,
  cursor: "pointer",
};
